/**
 * The serialized value forms required by D-039.
 *
 * The rule that shapes this whole file: **exclusion must be distinguishable
 * from absence, and neither may be a bare `null`.** A reader — human or
 * ChatGPT — must be able to tell "the user chose not to share this" from "this
 * was never recorded", because those two mean opposite things when judging a
 * week. A `null` collapses them.
 *
 * Every present value also carries its unit, and every derived value carries
 * the algorithm that produced it, so no figure in an export is ambiguous about
 * what it measures or how it was computed.
 */

export interface PresentValue<T> {
  readonly status: 'present'
  readonly value: T
  readonly unit?: string
  readonly algorithm?: string
}

export interface ExcludedValue {
  readonly status: 'excluded'
  /** The selection path that excluded it, e.g. `privacySelection.includeNotes`. */
  readonly reason: string
}

export interface MissingValue {
  readonly status: 'missing'
  /** Why it is absent, e.g. `noReadingRecorded`. */
  readonly reason: string
}

export type ExportValue<T> = PresentValue<T> | ExcludedValue | MissingValue

export function present<T>(value: T, unit?: string, algorithm?: string): PresentValue<T> {
  const base = { status: 'present' as const, value }
  if (unit !== undefined && algorithm !== undefined) return { ...base, unit, algorithm }
  if (unit !== undefined) return { ...base, unit }
  if (algorithm !== undefined) return { ...base, algorithm }
  return base
}

export function excluded(selectionPath: string): ExcludedValue {
  return { status: 'excluded', reason: selectionPath }
}

export function missing(cause: string): MissingValue {
  return { status: 'missing', reason: cause }
}

/**
 * Present when the value exists, `missing` with the given cause when it does
 * not. Never `null`.
 */
export function presentOrMissing<T>(
  value: T | undefined,
  cause: string,
  unit?: string,
  algorithm?: string,
): ExportValue<T> {
  return value === undefined ? missing(cause) : present(value, unit, algorithm)
}

/**
 * Excluded when the user opted out, otherwise present-or-missing.
 *
 * Exclusion wins over absence: if the user chose not to share notes, the export
 * says so rather than revealing, by saying "missing", that there were none.
 */
export function gated<T>(
  included: boolean,
  selectionPath: string,
  value: T | undefined,
  cause: string,
  unit?: string,
  algorithm?: string,
): ExportValue<T> {
  if (!included) return excluded(selectionPath)
  return presentOrMissing(value, cause, unit, algorithm)
}
